import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeAvailability } from "@/components/TimeAvailability";
import { PhoneVerification } from "@/components/PhoneVerification";
import { SchoolSelect } from "@/components/SchoolSelect";
import {
  SCHOLAR_GRADES,
  SCHOLAR_SCHOOL_OPTIONS,
  GENDER_OPTIONS,
  validateScholarForm,
  type ScholarFormData,
} from "@/lib/formSchema";
import { ArrowLeft, Loader2 } from "lucide-react";

interface ScholarFormProps {
  onBack: () => void;
  onSuccess: () => void;
}

export function ScholarForm({ onBack, onSuccess }: ScholarFormProps) {
  const [formData, setFormData] = useState<Partial<ScholarFormData>>({
    firstName: "",
    lastName: "",
    guardianFirstName: "",
    guardianLastName: "",
    guardianEmail: "",
    guardianPhone: "",
    grade: "",
    gender: "",
    school: "",
    timesAvailable: [],
    smsOptIn: false,
  });
  const [confirmGuardianEmail, setConfirmGuardianEmail] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const updateField = <K extends keyof ScholarFormData>(field: K, value: ScholarFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateScholarForm(formData);
    if (formData.guardianEmail && confirmGuardianEmail !== formData.guardianEmail) {
      validationErrors.push("Guardian email addresses do not match");
    }
    if (!phoneVerified) {
      validationErrors.push("Please verify the guardian's phone number");
    }
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const response = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "scholar", ...formData }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to submit form");
      }

      onSuccess();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : "Failed to submit form. Please try again."]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-8 px-4">
      <div className="container mx-auto max-w-2xl">
        <Button variant="ghost" onClick={onBack} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-emerald-900">Scholar Sign Up</CardTitle>
            <CardDescription>
              Register as a scholar to connect with a mentor and unlock your potential.
            </CardDescription>
            <a
              href="https://educationaljustice.org/wp-content/uploads/2025/09/1000-Family-Scholarship-2025-26.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 group flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200/60 text-sm font-medium text-emerald-800 hover:from-emerald-100 hover:to-amber-100 transition-all animate-pulse-subtle"
            >
              <span className="text-lg">💵</span>
              <span>Scholars can win a $1,000 Scholarship!</span>
              <span className="text-emerald-600 group-hover:translate-x-0.5 transition-transform">→</span>
            </a>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.length > 0 && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4">
                  <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                    {errors.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Scholar Information */}
              <div>
                <h3 className="text-lg font-medium mb-4 text-emerald-800">Scholar Information</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => updateField("firstName", e.target.value)}
                      placeholder="Enter scholar's first name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => updateField("lastName", e.target.value)}
                      placeholder="Enter scholar's last name"
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="grade">Current Grade</Label>
                  <Select value={formData.grade} onValueChange={(value) => updateField("grade", value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHOLAR_GRADES.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade} Grade
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender} onValueChange={(value) => updateField("gender", value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="school">School</Label>
                <SchoolSelect
                  value={formData.school || ""}
                  onChange={(value) => updateField("school", value)}
                  options={SCHOLAR_SCHOOL_OPTIONS}
                  placeholder="Search for scholar's school..."
                />
              </div>

              {/* Guardian Information */}
              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium mb-4 text-emerald-800">Parent/Guardian Information</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="guardianFirstName">Guardian First Name</Label>
                    <Input
                      id="guardianFirstName"
                      value={formData.guardianFirstName}
                      onChange={(e) => updateField("guardianFirstName", e.target.value)}
                      placeholder="Enter guardian's first name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guardianLastName">Guardian Last Name</Label>
                    <Input
                      id="guardianLastName"
                      value={formData.guardianLastName}
                      onChange={(e) => updateField("guardianLastName", e.target.value)}
                      placeholder="Enter guardian's last name"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="guardianEmail">Guardian Email</Label>
                <Input
                  id="guardianEmail"
                  type="email"
                  value={formData.guardianEmail}
                  onChange={(e) => updateField("guardianEmail", e.target.value)}
                  placeholder="guardian@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmGuardianEmail">Confirm Guardian Email</Label>
                <Input
                  id="confirmGuardianEmail"
                  type="email"
                  value={confirmGuardianEmail}
                  onChange={(e) => {
                    setConfirmGuardianEmail(e.target.value);
                    setErrors([]);
                  }}
                  placeholder="Re-enter guardian's email"
                />
              </div>

              <TimeAvailability
                value={formData.timesAvailable || []}
                onChange={(value) => updateField("timesAvailable", value)}
              />

              <PhoneVerification
                phone={formData.guardianPhone || ""}
                onPhoneChange={(phone) => updateField("guardianPhone", phone)}
                onVerified={setPhoneVerified}
                isVerified={phoneVerified}
                accentColor="emerald"
              />

              <div className="flex items-start space-x-3 rounded-md border p-4">
                <Checkbox
                  id="smsOptIn"
                  checked={formData.smsOptIn}
                  onCheckedChange={(checked) => updateField("smsOptIn", checked)}
                />
                <label
                  htmlFor="smsOptIn"
                  className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                >
                  I agree to receive text messages from Educational Justice at the phone number provided. Messages may include mentoring communications, reminders, and program updates. Message frequency varies. Message & data rates may apply. Reply STOP to opt out.
                </label>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                By signing up, you agree to our{" "}
                <a
                  href="https://educationaljustice.org/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:underline"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://educationaljustice.org/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:underline"
                >
                  Terms of Service
                </a>
                .
              </p>

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" size="lg" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Sign Up as Scholar"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
